/**
 * Monthly Mock Data for BrandHealth 360
 * Data for June 2025 - December 2025
 */

export interface MonthlyBrandData {
  brand_id: string;
  name: string;
  metrics: {
    awareness: number;
    consideration: number;
    usage: number;
    recent_usage: number;
    primary_brand: number;
    loyalty: number;
    nps: number;
    trust: number;
    pride: number;
    innovation: number;
    value: number;
    quality: number;
    taste: number;
    texture: number;
    natural: number;
  };
  digital: {
    sentiment_score: number;
    mentions_count: number;
    share_of_voice: number;
    engagement_rate: number;
  };
  equity: {
    trad_equity: number;
    digital_equity: number;
    total_equity: number;
    brand_index: number;
    community_index: number;
  };
  respondent_count: number;
}

export interface MonthlyData {
  [month: string]: {
    [brand_id: string]: MonthlyBrandData;
  };
}

// Available months for the date filter
export const availableMonths = [
  { value: '2025-06', label: 'June 2025' },
  { value: '2025-07', label: 'July 2025' },
  { value: '2025-08', label: 'August 2025' },
  { value: '2025-09', label: 'September 2025' },
  { value: '2025-10', label: 'October 2025' },
  { value: '2025-11', label: 'November 2025' },
  { value: '2025-12', label: 'December 2025' },
];

// Base data for each brand (December 2025 - most recent)
const baseBrandData: { [brand_id: string]: MonthlyBrandData } = {
  'coca-cola': {
    brand_id: 'coca-cola',
    name: 'Coca-Cola',
    metrics: {
      awareness: 0.92, consideration: 0.78, usage: 0.65, recent_usage: 0.58,
      primary_brand: 0.42, loyalty: 0.72, nps: 45,
      trust: 0.85, pride: 0.68, innovation: 0.62, value: 0.71,
      quality: 0.88, taste: 0.91, texture: 0.75, natural: 0.45
    },
    digital: {
      sentiment_score: 78, mentions_count: 45000, share_of_voice: 32, engagement_rate: 5.2
    },
    equity: {
      trad_equity: 85.5, digital_equity: 78.2, total_equity: 81.9, brand_index: 88.3, community_index: 75.6
    },
    respondent_count: 1250
  },
  'pepsi': {
    brand_id: 'pepsi',
    name: 'Pepsi',
    metrics: {
      awareness: 0.88, consideration: 0.72, usage: 0.58, recent_usage: 0.52,
      primary_brand: 0.35, loyalty: 0.65, nps: 38,
      trust: 0.78, pride: 0.62, innovation: 0.68, value: 0.75,
      quality: 0.82, taste: 0.85, texture: 0.72, natural: 0.42
    },
    digital: {
      sentiment_score: 72, mentions_count: 38000, share_of_voice: 28, engagement_rate: 4.8
    },
    equity: {
      trad_equity: 76.8, digital_equity: 82.4, total_equity: 79.6, brand_index: 80.2, community_index: 79.0
    },
    respondent_count: 1180
  },
  'sprite': {
    brand_id: 'sprite',
    name: 'Sprite',
    metrics: {
      awareness: 0.82, consideration: 0.65, usage: 0.48, recent_usage: 0.42,
      primary_brand: 0.22, loyalty: 0.58, nps: 32,
      trust: 0.75, pride: 0.55, innovation: 0.52, value: 0.78,
      quality: 0.79, taste: 0.82, texture: 0.68, natural: 0.55
    },
    digital: {
      sentiment_score: 68, mentions_count: 22000, share_of_voice: 18, engagement_rate: 4.2
    },
    equity: {
      trad_equity: 68.3, digital_equity: 71.5, total_equity: 69.9, brand_index: 72.1, community_index: 67.7
    },
    respondent_count: 980
  },
  'fanta': {
    brand_id: 'fanta',
    name: 'Fanta',
    metrics: {
      awareness: 0.78, consideration: 0.58, usage: 0.42, recent_usage: 0.35,
      primary_brand: 0.18, loyalty: 0.52, nps: 28,
      trust: 0.72, pride: 0.58, innovation: 0.65, value: 0.72,
      quality: 0.75, taste: 0.78, texture: 0.65, natural: 0.48
    },
    digital: {
      sentiment_score: 65, mentions_count: 18000, share_of_voice: 14, engagement_rate: 3.8
    },
    equity: {
      trad_equity: 62.7, digital_equity: 65.8, total_equity: 64.3, brand_index: 66.5, community_index: 62.1
    },
    respondent_count: 850
  },
  'mountain-dew': {
    brand_id: 'mountain-dew',
    name: 'Mountain Dew',
    metrics: {
      awareness: 0.72, consideration: 0.52, usage: 0.38, recent_usage: 0.32,
      primary_brand: 0.15, loyalty: 0.48, nps: 25,
      trust: 0.68, pride: 0.52, innovation: 0.72, value: 0.68,
      quality: 0.72, taste: 0.75, texture: 0.62, natural: 0.38
    },
    digital: {
      sentiment_score: 70, mentions_count: 15000, share_of_voice: 12, engagement_rate: 5.5
    },
    equity: {
      trad_equity: 71.2, digital_equity: 76.9, total_equity: 74.1, brand_index: 75.8, community_index: 72.4
    },
    respondent_count: 720
  }
};

// Monthly variation factors - representing seasonal and campaign effects
const monthlyVariations: { [month: string]: { [brand_id: string]: number } } = {
  '2025-06': { 'coca-cola': 0.92, 'pepsi': 0.94, 'sprite': 0.96, 'fanta': 0.95, 'mountain-dew': 0.93 }, // Summer start
  '2025-07': { 'coca-cola': 0.95, 'pepsi': 0.96, 'sprite': 1.02, 'fanta': 1.01, 'mountain-dew': 0.98 }, // Peak summer
  '2025-08': { 'coca-cola': 0.97, 'pepsi': 0.98, 'sprite': 1.05, 'fanta': 1.03, 'mountain-dew': 1.00 }, // Summer high
  '2025-09': { 'coca-cola': 0.98, 'pepsi': 0.97, 'sprite': 0.99, 'fanta': 0.98, 'mountain-dew': 0.99 }, // Back to school
  '2025-10': { 'coca-cola': 0.99, 'pepsi': 0.99, 'sprite': 0.97, 'fanta': 0.96, 'mountain-dew': 1.01 }, // Fall
  '2025-11': { 'coca-cola': 1.00, 'pepsi': 0.98, 'sprite': 0.96, 'fanta': 0.95, 'mountain-dew': 1.02 }, // Pre-holiday
  '2025-12': { 'coca-cola': 1.00, 'pepsi': 1.00, 'sprite': 1.00, 'fanta': 1.00, 'mountain-dew': 1.00 }, // Holiday/baseline
};

// NPS variations by month (absolute changes)
const npsVariations: { [month: string]: { [brand_id: string]: number } } = {
  '2025-06': { 'coca-cola': -5, 'pepsi': -3, 'sprite': -2, 'fanta': -4, 'mountain-dew': -6 },
  '2025-07': { 'coca-cola': -3, 'pepsi': -2, 'sprite': 2, 'fanta': 1, 'mountain-dew': -3 },
  '2025-08': { 'coca-cola': -2, 'pepsi': 0, 'sprite': 5, 'fanta': 3, 'mountain-dew': -1 },
  '2025-09': { 'coca-cola': -1, 'pepsi': -1, 'sprite': 2, 'fanta': 0, 'mountain-dew': 0 },
  '2025-10': { 'coca-cola': 0, 'pepsi': -1, 'sprite': 0, 'fanta': -1, 'mountain-dew': 2 },
  '2025-11': { 'coca-cola': 1, 'pepsi': 0, 'sprite': -1, 'fanta': -2, 'mountain-dew': 3 },
  '2025-12': { 'coca-cola': 0, 'pepsi': 0, 'sprite': 0, 'fanta': 0, 'mountain-dew': 0 },
};

// Seeded pseudo-random number generator for deterministic data
function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

// Generate a seed from string (month + brandId)
function stringToSeed(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

// Generate monthly data with deterministic variations
function generateMonthlyData(): MonthlyData {
  const data: MonthlyData = {};

  availableMonths.forEach(({ value: month }) => {
    data[month] = {};

    Object.keys(baseBrandData).forEach(brandId => {
      const base = baseBrandData[brandId];
      const variation = monthlyVariations[month]?.[brandId] || 1;
      const npsVar = npsVariations[month]?.[brandId] || 0;

      // Create a seeded random generator for this month/brand combination
      const seed = stringToSeed(`${month}-${brandId}`);
      const random = seededRandom(seed);

      // Apply variation with deterministic noise for realism
      const applyVariation = (val: number, isPercentage = true): number => {
        const varied = val * variation;
        const noise = (random() - 0.5) * 0.02; // ±1% noise
        const result = varied + (isPercentage ? noise : noise * 10);
        return isPercentage ? Math.min(1, Math.max(0, result)) : result;
      };

      data[month][brandId] = {
        brand_id: brandId,
        name: base.name,
        metrics: {
          awareness: applyVariation(base.metrics.awareness),
          consideration: applyVariation(base.metrics.consideration),
          usage: applyVariation(base.metrics.usage),
          recent_usage: applyVariation(base.metrics.recent_usage),
          primary_brand: applyVariation(base.metrics.primary_brand),
          loyalty: applyVariation(base.metrics.loyalty),
          nps: Math.round(base.metrics.nps + npsVar + (random() - 0.5) * 4),
          trust: applyVariation(base.metrics.trust),
          pride: applyVariation(base.metrics.pride),
          innovation: applyVariation(base.metrics.innovation),
          value: applyVariation(base.metrics.value),
          quality: applyVariation(base.metrics.quality),
          taste: applyVariation(base.metrics.taste),
          texture: applyVariation(base.metrics.texture),
          natural: applyVariation(base.metrics.natural),
        },
        digital: {
          sentiment_score: Math.round(base.digital.sentiment_score * variation + (random() - 0.5) * 5),
          mentions_count: Math.round(base.digital.mentions_count * variation * (0.9 + random() * 0.2)),
          share_of_voice: Math.round(base.digital.share_of_voice * variation + (random() - 0.5) * 2),
          engagement_rate: Number((base.digital.engagement_rate * variation + (random() - 0.5) * 0.5).toFixed(1)),
        },
        equity: {
          trad_equity: Number((base.equity.trad_equity * variation + (random() - 0.5) * 3).toFixed(1)),
          digital_equity: Number((base.equity.digital_equity * variation + (random() - 0.5) * 3).toFixed(1)),
          total_equity: Number((base.equity.total_equity * variation + (random() - 0.5) * 3).toFixed(1)),
          brand_index: Number((base.equity.brand_index * variation + (random() - 0.5) * 3).toFixed(1)),
          community_index: Number((base.equity.community_index * variation + (random() - 0.5) * 3).toFixed(1)),
        },
        respondent_count: Math.round(base.respondent_count * (0.95 + random() * 0.1)),
      };
    });
  });

  return data;
}

// Pre-generated monthly data
export const monthlyData = generateMonthlyData();

// Helper function to get data for a specific month and brand
export function getMonthlyBrandData(month: string, brandId: string): MonthlyBrandData | null {
  return monthlyData[month]?.[brandId] || null;
}

// Helper function to get all brands for a specific month
export function getMonthlyAllBrands(month: string): MonthlyBrandData[] {
  const monthData = monthlyData[month];
  if (!monthData) return [];
  return Object.values(monthData);
}

// Helper function to get trend data for a brand across all months
export function getBrandTrendData(brandId: string): { month: string; data: MonthlyBrandData }[] {
  return availableMonths.map(({ value: month }) => ({
    month,
    data: monthlyData[month][brandId],
  })).filter(item => item.data);
}

// Get comparison data for multiple brands for a specific month
export function getComparisonData(month: string, brandIds: string[]): MonthlyBrandData[] {
  const monthData = monthlyData[month];
  if (!monthData) return [];
  return brandIds.map(id => monthData[id]).filter(Boolean);
}

// Default month (most recent)
export const defaultMonth = '2025-12';
